import axios from 'axios'
import { AUTO3P_BACKEND_URL as baseURL } from 'config'
import { useNavigate, useLocation } from 'react-router-dom'

import { LOGIN_PATH as ROUTE_LOGIN_PATH, MECHANIC_EVENTS_PATH } from '@/utils/routePaths'

import { LOGIN_PATH } from '@/services/endpoints'

import {
  clearTokenLoginFailed,
  endTokenLogin,
  markTokenLoginFailed,
  tryBeginTokenLogin,
} from '@/blocks/Auth/tokenLoginGuard'

import { useAuthDataHandler } from '../hooks/useAuthDataHandler'

export const useAuthActions = () => {
  const api = axios.create({ baseURL })
  const navigate = useNavigate()
  const location = useLocation()

  const {
    saveAuthTokens,
    saveLoginSessionData,
    updateLoginSessionData,
    clearUserData,
    getLoginSessionData,
    getDefaultHomePage,
  } = useAuthDataHandler()

  const handle2FactorLogin = async ({
    loginToken,
    tfaCode,
    password,
    needLoginToken,
  }) => {
    const { data } = await api.post(LOGIN_PATH, {
      token: loginToken,
      tfaCode,
      needLoginToken,
      ...(password ? { password } : {}),
    })

    const { JWTToken: jwtToken, ...loginData } = data

    if (jwtToken) {
      saveAuthTokens({ jwtToken, loginToken })
      saveLoginSessionData(loginData)
      clearTokenLoginFailed()
    }

    return data
  }

  const handleStandardLogin = async ({
    username,
    password,
    needLoginToken: isRememberMeEnabled,
  }) => {
    try {
      const { data } = await api.post(LOGIN_PATH, {
        userId: username,
        password,
        needLoginToken: isRememberMeEnabled,
      })

      const { JWTToken: jwtToken, loginToken, ...loginData } = data

      if (jwtToken) {
        saveAuthTokens({ jwtToken, loginToken, isRememberMeEnabled })
        saveLoginSessionData(loginData)
        clearTokenLoginFailed()
      }

      return data
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const handleLoginWithRememberMe = async ({
    loginToken,
    ...entityDetails
  }) => {
    if (!tryBeginTokenLogin()) {
      return Promise.reject(new Error('Token login already failed or in progress'))
    }

    try {
      const {
        data: { JWTToken: jwtToken, ...loginData },
      } = await api.post(LOGIN_PATH, {
        token: loginToken,
        entity: entityDetails?.entityId,
      })

      saveAuthTokens({ jwtToken })

      updateLoginSessionData({
        settings: loginData.settings,
        services: loginData.services,
        ...(entityDetails ? { activeEntity: entityDetails } : {}),
      })

      clearTokenLoginFailed()

      return { jwtToken, ...loginData }
    } catch (error) {
      markTokenLoginFailed()
      return Promise.reject(error)
    } finally {
      endTokenLogin()
    }
  }

  const handleSilentEntityLogin = async ({ loginToken, ...entityDetails }) => {
    await handleLoginWithRememberMe({ loginToken, ...entityDetails })
    const { services } = getLoginSessionData()
    const defaultHomePage = getDefaultHomePage(services)
    navigate(defaultHomePage)
    window.location.reload()
  }

  const handleLogout = ({
    isJwtOnlyLogout = false,
    shouldPreserveLocation = false,
  } = {}) => {
    const isMechanicEvents = location.pathname.includes(MECHANIC_EVENTS_PATH)
    clearUserData(isJwtOnlyLogout)
    redirectToLogin(shouldPreserveLocation || isMechanicEvents)
  }

  const redirectToLogin = (shouldPreserveLocation) => {
    if (!shouldPreserveLocation) {
      return navigate(ROUTE_LOGIN_PATH)
    }

    const redirectPath = location.pathname + location.search

    return navigate(
      ROUTE_LOGIN_PATH + '?redirect-url=' + encodeURIComponent(redirectPath),
    )
  }

  return {
    handle2FactorLogin,
    handleStandardLogin,
    handleLoginWithRememberMe,
    handleSilentEntityLogin,
    handleLogout,
  }
}
